import {
  EmptyState,
  FlexColumn,
  FlexResponsive,
  FlexRow,
  FullContainer,
  isValidLatitude,
  isValidLongitude,
  LabeledData,
  useMoment,
} from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { StyledLink } from 'baseui/link';
import { LabelMedium, ParagraphSmall } from 'baseui/typography';
import { Marker } from 'react-map-gl/mapbox';
import { ASSET_TYPE_CONTAINER } from '../../../../constants/asset';
import { useStyles } from '../../../../hooks/useStyles';
import { FleetDriverDetail } from '../../../../types/drivers';
import { getAssetTitle, isVehicleKind } from '../../../../utils/assets';
import VehicleIcon from '../../../map/markers/VehicleIcon';
import StaticMap from '../../../map/StaticMap';
import AssetData from '../../../shared/AssetData';
import ContainerData from '../../../shared/ContainerData';
import OperationalStatusLabel from '../../../shared/OperationalStatusLabel';
import OperationalStatusTime from '../../../shared/OperationalStatusTime';
import VehicleData from '../../../shared/VehicleData';
import { StyledTooltip } from '../../../ui/StyledTooltip';

type Props = Readonly<{
  driver: FleetDriverDetail;
}>;

function FleetDriverCurrentState({ driver }: Props) {
  const { theme, css, classes } = useStyles();
  const { formatDateFromTs } = useMoment();
  const labelWidth = '80px';
  const currentState = driver.cs;

  return (
    <FullContainer verticalFlex>
      <FlexColumn
        classNames={css({
          flex: 1,
          overflow: 'auto',
        })}
        gap={theme.sizing.scale300}
      >
        {!currentState && <EmptyState title={'No existe un registro para este conductor.'} />}
        {currentState && (
          <FlexColumn>
            <FlexColumn gap={theme.sizing.scale200}>
              <FlexColumn gap={0}>
                <LabelMedium>{getAssetTitle(currentState.asset)}</LabelMedium>
                <StyledTooltip
                  content="Etiqueta"
                  placement={'top'}
                >
                  <ParagraphSmall
                    margin={0}
                    color={theme.colors.contentSecondary}
                  >
                    {`${currentState.asset.label}`}
                  </ParagraphSmall>
                </StyledTooltip>
              </FlexColumn>
              <FlexRow
                gap={theme.sizing.scale300}
                classNames={classes.centeredStart}
              >
                {currentState.operationalState && (
                  <OperationalStatusTime operationalState={currentState.operationalState} />
                )}
                {currentState.operationalState && (
                  <OperationalStatusLabel
                    operationalState={currentState.operationalState}
                    positionDirection={currentState.position_direction}
                    speed={currentState.position_speed}
                  />
                )}
              </FlexRow>
            </FlexColumn>
            <FlexResponsive>
              <FlexColumn
                gap={theme.sizing.scale300}
                classNames={css({ flex: 2, width: '100%', alignSelf: 'start' })}
              >
                {isValidLatitude(currentState.position_latitude) &&
                  isValidLongitude(currentState.position_longitude) && (
                    <Block
                      className={css({
                        height: '250px',
                        borderRadius: theme.borders.radius400,
                        overflow: 'hidden',
                      })}
                    >
                      <StaticMap
                        center={{
                          latitude: currentState.position_latitude,
                          longitude: currentState.position_longitude,
                        }}
                        markers={
                          <Marker
                            latitude={currentState.position_latitude}
                            longitude={currentState.position_longitude}
                            rotation={currentState.position_direction || 0}
                          >
                            <VehicleIcon
                              color={currentState.asset.vehicle?.color}
                              assetType={currentState.asset.assetType}
                              vehicleType={currentState.asset.vehicle?.vehicleType}
                            />
                          </Marker>
                        }
                      />
                    </Block>
                  )}
                <LabeledData
                  label={'Dirección:'}
                  labelWidth={labelWidth}
                  content={
                    <StyledLink
                      href={`https://maps.google.com/?q=${currentState.position_latitude},${currentState.position_longitude}`}
                      target="_blank"
                      style={{ color: theme.colors.contentSecondary, ...theme.typography.ParagraphSmall }}
                    >
                      {currentState.geoReference}
                    </StyledLink>
                  }
                />
                <LabeledData
                  label={'Velocidad:'}
                  content={`${currentState.position_speed} Km/h`}
                  labelWidth={labelWidth}
                />
                <LabeledData
                  label={'Fecha:'}
                  content={`${formatDateFromTs(+currentState.timestamp)}`}
                  labelWidth={labelWidth}
                />
              </FlexColumn>

              <FlexColumn classNames={css({ flex: 3 })}>
                <AssetData
                  asset={currentState.asset}
                  ident={currentState.ident}
                />
                {currentState.asset.assetType === ASSET_TYPE_CONTAINER && (
                  <ContainerData container={currentState.asset.container} />
                )}
                {isVehicleKind(currentState.asset.assetType) && (
                  <VehicleData
                    vehicle={currentState.asset.vehicle}
                    odometer={currentState.gnss_vehicle_mileage}
                    hourometer={currentState.engine_motorhours}
                  />
                )}
              </FlexColumn>
            </FlexResponsive>
          </FlexColumn>
        )}
      </FlexColumn>
    </FullContainer>
  );
}

export default FleetDriverCurrentState;
